/**
 * Pot painting — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_paint_crafts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const potPaintingPack: ToilePack = {
    slug: 'pot-painting',
    category: 'craft',
    name: 'Pot painting',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'warli', label: 'Warli', subject: 'a terracotta pot painted with Warli figures in white'},
        {key: 'geometric', label: 'Geometric', subject: 'a pot painted in geometric shapes'},
        {key: 'face', label: 'Face', subject: 'a pot painted with a face'},
    ],
    styles: [
        {key: 'earthy', label: 'Earthy', style: 'matte acrylic on raw terracotta, the clay left bare in places', palette: 'white, black and terracotta red', framing: 'a small succulent planted in it, on a wooden balcony ledge'},
        {key: 'bright', label: 'Bright', style: 'glossy acrylic in flat bold blocks sealed with varnish', palette: 'turquoise, mustard, coral and white', framing: 'planted with a leafy pothos, on a white windowsill'},
        {key: 'pastel', label: 'Pastel', style: 'soft chalk-finish paint with dotted accents', palette: 'blush, sage and lavender', framing: 'planted with a fern, on a garden table among gravel'},
    ],
    directions: ['cactus pot', 'mandala', 'hanging pot'],
    directionHint: 'try: cactus pot',
    seo: {
        title: 'Pot painting: Warli, geometric, face pots',
        metaDescription: 'Nine terracotta pot painting ideas: Warli, geometric and face designs in earthy, bright and pastel finishes, with a plant.',
        h1: 'Terracotta pot painting: Warli, geometric, face',
        tagline: 'Three painted pots, each in an earthy, a bright or a pastel finish, shown with a plant.',
        intro: 'Painted terracotta pots make a plant corner more personal. These nine show three designs: a pot with white Warli figures, a pot in geometric shapes and a pot with a face. Each is painted in matte earthy colours, glossy bright blocks or soft pastels, and shown with a plant in it on a balcony, a windowsill or a garden table. Type a design, such as a cactus pot or a mandala, to ask for it.',
        useCases: [
            'a planter for a balcony or windowsill',
            'a gift pot for a plant lover',
            'a garden table decoration',
            'a pot for a classroom plant project',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Warli, geometric and face pots, each in an earthy, bright or pastel finish, shown with a plant.'},
            {q: 'Are these photos of real pots?', a: 'No. They are generated pictures of each design, not photographs of pots someone painted.'},
            {q: 'What is the difference between the three finishes?', a: 'Earthy is matte acrylic with the clay left bare in places. Bright is glossy acrylic in flat blocks, sealed with varnish. Pastel is a soft chalk finish with dotted accents.'},
            {q: 'Can I ask for a different design?', a: 'Yes. Type a design such as a cactus pot, a mandala or a hanging pot. A typed design is a request, not a promised result.'},
        ],
    },
}
